import {FC, ReactNode} from 'react'
import './boarding-pass.scss'

export type BoardingPassStatusTone = 'available' | 'occupied' | 'reserved' | 'neutral'

export interface BoardingPassField {
  label: string
  value: ReactNode
}

export interface BoardingPassCardProps {
  /** Big primary line, e.g. a renter name or "Twin Bed". */
  title: string
  /** Optional secondary line under the title. */
  subtitle?: string
  /** Small uppercase brand line. Defaults to the platform name. */
  brand?: string
  /** Small uppercase document-type line, e.g. "Bed Pass", "Booking". */
  docType?: string
  /** Label/value pairs laid out in the body grid. */
  fields?: BoardingPassField[]
  /** Large code shown on the tear-off stub, e.g. a bed code like "5Q". */
  stubCode?: string
  /** Caption under the stub code, e.g. "Bed". */
  stubLabel?: string
  /** Optional status pill text + tone. */
  status?: {label: string; tone?: BoardingPassStatusTone}
  className?: string
}

/**
 * The signature boarding-pass card. An airline-ticket layout — a main panel
 * plus a perforated tear-off stub — used across Beds, Bookings, and the Flyer
 * Automation module so the whole platform shares one visual identity.
 */
export const BoardingPassCard: FC<BoardingPassCardProps> = ({
  title,
  subtitle,
  brand = 'Crew Crashpad',
  docType = 'Boarding Pass',
  fields = [],
  stubCode,
  stubLabel,
  status,
  className = '',
}) => {
  return (
    <div className={`ccp-boarding-pass ${className}`.trim()}>
      <div className='ccp-boarding-pass__main'>
        <div className='ccp-boarding-pass__header'>
          <span className='ccp-boarding-pass__brand'>{brand}</span>
          <span className='ccp-boarding-pass__doc-type'>{docType}</span>
        </div>

        <h3 className='ccp-boarding-pass__title'>{title}</h3>
        {subtitle && <p className='ccp-boarding-pass__subtitle'>{subtitle}</p>}

        {status && (
          <span
            className={`ccp-boarding-pass__status ccp-boarding-pass__status--${
              status.tone ?? 'neutral'
            }`}
          >
            {status.label}
          </span>
        )}

        {fields.length > 0 && (
          <div className='ccp-boarding-pass__fields' style={{marginTop: '1rem'}}>
            {fields.map((f, i) => (
              <div key={`${f.label}-${i}`}>
                <div className='ccp-boarding-pass__field-label'>{f.label}</div>
                <div className='ccp-boarding-pass__field-value'>{f.value}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {(stubCode || stubLabel) && (
        <>
          <div className='ccp-boarding-pass__perforation' />
          <div className='ccp-boarding-pass__stub'>
            <div>
              {stubLabel && <div className='ccp-boarding-pass__stub-label'>{stubLabel}</div>}
              {stubCode && <div className='ccp-boarding-pass__stub-code'>{stubCode}</div>}
            </div>
            <div className='ccp-boarding-pass__barcode' aria-hidden='true' />
          </div>
        </>
      )}
    </div>
  )
}

export default BoardingPassCard
